// src/components/CustomerDetail.tsx
import React, { useState, useEffect } from 'react';
import { mockDataService, Customer, Ticket, ApplicationEvent } from '../services/MockDataService';

interface CustomerDetailProps {
  phoneNumber: string;
  onBack?: () => void;
  onTicketSelect?: (ticketId: string) => void;
  onEventsClick?: () => void;
}

const CustomerDetail: React.FC<CustomerDetailProps> = ({ phoneNumber, onBack, onTicketSelect }) => {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [events, setEvents] = useState<ApplicationEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'tickets' | 'events'>('overview');

  useEffect(() => {
    const loadCustomerData = async () => {
      setLoading(true);
      try {
        const [customerData, ticketsData, eventsData] = await Promise.all([
          mockDataService.getCustomer(phoneNumber),
          mockDataService.getCustomerTickets(phoneNumber),
          mockDataService.getEvents(phoneNumber, 50)
        ]);
        
        setCustomer(customerData);
        setTickets(ticketsData);
        setEvents(eventsData);
      } catch (error) {
        console.error('Error loading customer data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCustomerData();
  }, [phoneNumber]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading customer details...</p>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <span className="text-6xl mb-4 block">😕</span>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Customer Not Found</h2>
          <p className="text-gray-600 mb-4">No customer found with phone number: {phoneNumber}</p>
          <button 
            onClick={onBack}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
          >
            ← Back to Search
          </button>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status: Customer['customer_status']) => {
    const styles = {
      active: 'bg-green-100 text-green-800 border-green-200',
      pending_terms: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      pending_consent: 'bg-orange-100 text-orange-800 border-orange-200',
      unsubscribed: 'bg-red-100 text-red-800 border-red-200'
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${styles[status]}`}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const getTicketStatusBadge = (status: Ticket['status']) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      open: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      fulfilled: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const getEventStatusIcon = (status: ApplicationEvent['status']) => {
    const icons = {
      success: '✅',
      failed: '❌',
      pending: '⏳',
      in_progress: '🔄'
    };
    return icons[status] || '❓';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatEventType = (eventType: string) => {
    return eventType.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center">
              <button 
                onClick={onBack}
                className="mr-4 text-gray-500 hover:text-gray-700"
              >
                ← Back
              </button>
              <h1 className="text-2xl font-bold text-gray-900">
                👤 Customer Detail
              </h1>
            </div>
            <div className="text-sm text-gray-500">
              Last updated: {formatDate(customer.first_seen_at)}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Customer Profile Card */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                {customer.phone_number}
              </h2>
              <div className="flex items-center space-x-4 mb-4">
                {getStatusBadge(customer.customer_status)}
                {customer.sms_opted_in && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    📱 SMS Opted In
                  </span>
                )}
                {customer.terms_accepted && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    ✅ Terms Accepted
                  </span>
                )}
              </div>
            </div>
            
            <div className="text-right">
              <p className="text-sm text-gray-600">
                <span className="font-medium">First Seen:</span> {formatDate(customer.first_seen_at)}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Channel:</span> {customer.initial_channel.toUpperCase()}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Terms Version:</span> {customer.terms_version}
              </p>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{tickets.length}</div>
              <div className="text-sm text-gray-600">Total Tickets</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {tickets.filter(t => t.status === 'fulfilled').length}
              </div>
              <div className="text-sm text-gray-600">Completed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {tickets.filter(t => ['open', 'in_progress'].includes(t.status)).length}
              </div>
              <div className="text-sm text-gray-600">Active</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{customer.conversation_ids.length}</div>
              <div className="text-sm text-gray-600">Conversations</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {(['overview', 'tickets', 'events'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  {tab === 'tickets' && ` (${tickets.length})`}
                  {tab === 'events' && ` (${events.length})`}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Customer Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium text-gray-700 mb-2">Contact Details</h4>
                      <dl className="space-y-2">
                        <div>
                          <dt className="text-sm text-gray-600">Phone Number</dt>
                          <dd className="text-sm font-medium">{customer.phone_number}</dd>
                        </div>
                        <div>
                          <dt className="text-sm text-gray-600">Initial Channel</dt>
                          <dd className="text-sm font-medium">{customer.initial_channel.toUpperCase()}</dd>
                        </div>
                        {customer.extracted_info.zip_code && (
                          <div>
                            <dt className="text-sm text-gray-600">ZIP Code</dt>
                            <dd className="text-sm font-medium">{customer.extracted_info.zip_code}</dd>
                          </div>
                        )}
                      </dl>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-700 mb-2">Account Status</h4>
                      <dl className="space-y-2">
                        <div>
                          <dt className="text-sm text-gray-600">Customer Status</dt>
                          <dd className="text-sm">{getStatusBadge(customer.customer_status)}</dd>
                        </div>
                        <div>
                          <dt className="text-sm text-gray-600">SMS Consent</dt>
                          <dd className="text-sm font-medium">
                            {customer.sms_opted_in ? (
                              <span className="text-green-600">✅ Opted In</span>
                            ) : (
                              <span className="text-red-600">❌ Not Opted In</span>
                            )}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-sm text-gray-600">Terms Accepted</dt>
                          <dd className="text-sm font-medium">
                            {customer.terms_accepted ? (
                              <span className="text-green-600">✅ Accepted ({customer.terms_version})</span>
                            ) : (
                              <span className="text-red-600">❌ Not Accepted</span>
                            )}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  </div>
                </div>

                {Object.keys(customer.extracted_info).length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">Extracted Information</h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(customer.extracted_info).map(([key, value]) => (
                          <div key={key}>
                            <dt className="text-sm text-gray-600">{key.replace('_', ' ').toUpperCase()}</dt>
                            <dd className="text-sm font-medium">{value}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tickets Tab */}
            {activeTab === 'tickets' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Customer Tickets</h3>
                  <span className="text-sm text-gray-600">{tickets.length} total tickets</span>
                </div>

                {tickets.length === 0 ? (
                  <div className="text-center py-8">
                    <span className="text-4xl mb-4 block">🎫</span>
                    <p className="text-gray-500">No tickets found for this customer.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {tickets.map((ticket) => (
                      <div
                        key={ticket.ticket_id}
                        className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                        onClick={() => onTicketSelect?.(ticket.ticket_id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h4 className="font-medium text-gray-900">{ticket.ticket_id}</h4>
                              {getTicketStatusBadge(ticket.status)}
                              {ticket.urgency_level && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  🚨 URGENT
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-2">
                              <span className="font-medium">Service:</span> {ticket.service_type.replace('_', ' ')}
                            </p>
                            <p className="text-sm text-gray-700">{ticket.issue_description}</p>
                            <p className="text-xs text-gray-500 mt-2">
                              Created: {formatDate(ticket.created_at)}
                            </p>
                          </div>
                          
                          <div className="text-right">
                            <div className="text-sm font-medium text-gray-900">
                              {ticket.completion_percentage}% Complete
                            </div>
                            <div className="w-20 bg-gray-200 rounded-full h-2 mt-1">
                              <div 
                                className="bg-blue-600 h-2 rounded-full" 
                                style={{ width: `${ticket.completion_percentage}%` }}
                              ></div>
                            </div>
                            <button className="text-blue-600 hover:text-blue-800 text-sm font-medium mt-2">
                              View Details →
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Events Tab */}
            {activeTab === 'events' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Event Timeline</h3>
                  <span className="text-sm text-gray-600">Last {events.length} events</span>
                </div>

                {events.length === 0 ? (
                  <div className="text-center py-8">
                    <span className="text-4xl mb-4 block">📅</span>
                    <p className="text-gray-500">No events found for this customer.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {events.map((event, index) => (
                      <div key={`${event.event_timestamp}-${index}`} className="flex items-start space-x-3 pb-3 border-b border-gray-100 last:border-b-0">
                        <div className="flex-shrink-0 text-lg">
                          {getEventStatusIcon(event.status)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-gray-900">
                              {formatEventType(event.event_type)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatDate(event.created_at)}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              event.status === 'success' ? 'bg-green-100 text-green-800' :
                              event.status === 'failed' ? 'bg-red-100 text-red-800' :
                              event.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {event.status.toUpperCase()}
                            </span>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {event.user_type.toUpperCase()}
                            </span>
                            {event.ticket_id && (
                              <span className="text-xs text-gray-600">
                                Ticket: {event.ticket_id}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default CustomerDetail;